import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewContainerRef,
  output,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { getProductCodenameFromSubscription } from '@core/billing-plans-legacy';
import { filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import type { FeatureDiscoveryFeatures } from '@core/feature-discovery-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { OnboardingVideo, OnboardingVideoId } from '@core/onboarding-lib-data';
import type { PricingFeatureLoadedModel } from '@core/pricing-legacy';
import { PricingFeatureObservableModel } from '@core/pricing-legacy';
import type { BillingPlanTier } from '@core/products-types';
import { IconComponent } from '@core/user-interface-legacy';

import { FeatureVideoAnalytics } from './infobox-video.analytics';
import { OnboardingVideosServiceBase } from '../../types/onboarding-videos-service.types';

@Component({
  selector: 'rt-infobox-video',
  templateUrl: './infobox-video.component.html',
  styleUrls: ['./infobox-video.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FeatureVideoAnalytics],
  imports: [InfoboxComponent, IconComponent, TranslatePipe],
})
export class InfoboxVideoComponent implements OnInit {
  @Input() public heading: string;
  @Input() public content: string;
  @Input() public video: OnboardingVideoId;
  @Input() public analyticsFeatureName: string;
  @Input() public hidden: boolean;
  @Input() public storageKey: string;
  @Input() public featureDiscoveryKey: keyof FeatureDiscoveryFeatures;
  @Input() public hideForBillingPlanTiers: Array<BillingPlanTier | 'trial'> = [];
  @Input() public hasNewBadge = false;
  @Input() public marginBottom = true;

  public dismissClick = output<void>();

  public onboardingVideo: OnboardingVideo;
  private ngOnDestroy$ = new Subject<void>();
  constructor(
    private onboardingVideosService: OnboardingVideosServiceBase,
    private viewContainerRef: ViewContainerRef,
    private i18nService: I18nService,
    private pricingFeatureObservableModel: PricingFeatureObservableModel,
    private cdRef: ChangeDetectorRef,
    private analytics: FeatureVideoAnalytics,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.checkSubscription();
    this.getVideo();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openOnboardingVideo() {
    this.analytics.trackOpenVideo(this.analyticsFeatureName);

    const dialogRef = this.onboardingVideosService.openVideoDialog(
      { ...this.onboardingVideo },
      this.viewContainerRef,
      false,
      {
        ctaButtonLabel: this.i18nService.translate('app.onboarding.walkthrough_tooltip.got_it'),
      },
    );

    dialogRef.afterClosed.pipe(takeUntil(this.ngOnDestroy$), filterBoolean).subscribe(() => {
      this.markFeatureUsed();
      this.onboardingVideosService.markAsWatched({
        id: this.onboardingVideo.id,
      });
    });
  }

  public handleDismissClick() {
    this.analytics.trackCloseInfobox(this.analyticsFeatureName);

    this.hidden = true;
    this.dismissClick.emit();
    this.markFeatureUsed();
    this.cdRef.detectChanges();
  }

  private getVideo() {
    this.onboardingVideosService
      .getVideo(this.video)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(video => {
        this.onboardingVideo = video;
        this.hidden = this.hidden || video.watched;

        this.cdRef.detectChanges();
      });
  }

  private checkSubscription() {
    this.pricingFeatureObservableModel
      .pipe(
        filter(model => model.loaded),
        map((model: PricingFeatureLoadedModel) =>
          this.hideForBillingPlanTiers.some(billingPlanTier => {
            if (billingPlanTier === 'trial') return model.trial;
            return getProductCodenameFromSubscription(model.subscription) === billingPlanTier;
          }),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(shouldHide => {
        this.hidden = this.hidden || shouldHide;
        this.cdRef.detectChanges();
      });
  }

  private markFeatureUsed(): void {
    if (this.featureDiscoveryKey) {
      this.store.dispatch(
        FeatureDiscoveryActions.update({ payload: { [this.featureDiscoveryKey]: true } }),
      );
    }
  }
}
